import { useLazyQuery, useMutation } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { INVITE_TOKEN } from '../../../../constants/common';
import { LOGIN_STEPS } from '../../../../constants/login';
import { SEND_OTP_EMAIL } from '../../../../apis/user';
import { VERIFY_WORKSPACE_INVITE } from '../../../../apis/workspace';
import Button from '../../../../components/atoms/button';
import GoogleIcon from '../../../../images/GoogleIcon.svg';
import Input from '../../../../components/atoms/input';
import useWorkspaceInvite from '../../../../context/WorkspaceInviteContext';
import useAuth from '../../../../context/AuthContext';

const LoginWithEmailOrSSO = (props) => {
  // init
  const { isLoggedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const { email, setEmail, setLoginStep, setIsLoading, setToast, ssoLogin, startTimer, previousEmailOtp, setPreviousEmailOtp, timeLeft } = props;
  const { isInviteUser, workspaceName } = useWorkspaceInvite();

  // mutations / queries
  const [generateLoginOTP] = useMutation(SEND_OTP_EMAIL);
  const [verifyWorkspaceInvite] = useLazyQuery(VERIFY_WORKSPACE_INVITE, { fetchPolicy: 'network-only' });

  const verifyInvite = async () => {
    try {
      const token = searchParams.get(INVITE_TOKEN);
      const { data, errors } = await verifyWorkspaceInvite({ variables: { token, email } });
      if (errors && !data) {
        setLoginStep(LOGIN_STEPS.LOGIN_ERROR);
        throw new Error('Something went wrong while verifying Invite!');
      }
    } catch (error) {
      setLoginStep(LOGIN_STEPS.LOGIN_ERROR);
      throw new Error('Something went wrong while verifying Invite!');
    }
  };

  const handleContinue = async (e) => {
    e.preventDefault();

    if (previousEmailOtp && previousEmailOtp === email && timeLeft > 0) {
      setLoginStep(LOGIN_STEPS.VERIFY_OTP);
      return;
    }

    try {
      setIsLoading(true);
      if (isInviteUser) {
        await verifyInvite();
        if (isLoggedIn) {
          setLoginStep(LOGIN_STEPS.COMPLETE_PROFILE);
          setIsLoading(false);
          return;
        }
      }

      const { data, loading, error } = await generateLoginOTP({ variables: { email } });
      if (data && !loading && !error) {
        startTimer();
        setPreviousEmailOtp(email);
        setLoginStep(LOGIN_STEPS.VERIFY_OTP);
      }
    } catch (error) {
      setToast((prevstate) => ({
        ...prevstate,
        message: error?.message || 'Something went wrong!',
        open: true,
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextChange = (event) => {
    setEmail(event.target.value);
  };

  return (
    <>
      <div>
        <h4 className="login-title">Welcome!</h4>
        {isInviteUser && <h4 style={{ marginTop: 5 }} className="login-title">Join {workspaceName}</h4>}
        <h4 className="login-subtitle">Enter your email address to get started with Simple Plan.</h4>
      </div>
      <form className="login-input-email" onSubmit={handleContinue}>
        <div className="border-card-input-container">
          <span className="border-card-label">Email Address</span>
          <Input autoFocus required placeHolder="user@work-email.com" type="email" value={email} onChange={handleTextChange} />
        </div>
        <Button fullWidth type="submit" primary><strong>Continue</strong></Button>
      </form>
      {
        !isInviteUser &&
        <>
          <span className="or-divider">
            <span className="divider" />
            <span className="or-text">or</span>
            <span className="divider" />
          </span>
          <Button fullWidth onClick={ssoLogin} primaryDark startIcon={<img src={GoogleIcon} alt="google" />}>
            <strong>Continue with Google</strong>
          </Button>
        </>
      }
    </>
  );
};

LoginWithEmailOrSSO.propTypes = {
  ssoLogin: PropTypes.func.isRequired,
  previousEmailOtp: PropTypes.string.isRequired,
  setPreviousEmailOtp: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  setEmail: PropTypes.func.isRequired,
  setLoginStep: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
  startTimer: PropTypes.func.isRequired,
  timeLeft: PropTypes.number.isRequired,
};

export default LoginWithEmailOrSSO;
